import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Plug, Play, CheckCircle, XCircle, ChevronDown, ChevronUp, Copy } from 'lucide-react';
import api from '../services/api';

const AUTH_TYPES = [
    { value:'none', label:'Sem autenticacao' },
    { value:'bearer', label:'Bearer Token' },
    { value:'api_key', label:'API Key (header)' },
    { value:'basic', label:'Basic Auth (user/pass)' },
    { value:'custom_header', label:'Headers customizados' },
    { value:'oauth', label:'OAuth (token automatico)' },
];

const METODOS = ['GET','POST','PUT','PATCH','DELETE'];

const emptyEndpoint = { nome:'', descricao:'', metodo:'GET', caminho:'', parametros:[], body_template:null, mapeamento_resposta:'', notificar:false, email_assunto:'', email_modelo:'' };
const emptyParam = { nome:'', tipo:'string', descricao:'', obrigatorio:false, in:'query' };

export default function Integracoes() {
    const [integracoes, setIntegracoes] = useState([]);
    const [funcs, setFuncs] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [showTestar, setShowTestar] = useState(false);
    const [editing, setEditing] = useState(null);
    const [expandedCard, setExpandedCard] = useState(null);
    const [testResult, setTestResult] = useState(null);
    const [testLoading, setTestLoading] = useState(false);
    const [testEndpoint, setTestEndpoint] = useState('');
    const [testParams, setTestParams] = useState({});

    const emptyForm = {
        funcionario_id:'', nome:'', descricao:'', tipo:'rest_api',
        base_url:'', auth_type:'none', auth_config:{},
        headers_custom:{}, endpoints:[], descricao_ia:'',
        timeout_ms:10000, retry_tentativas:1, resposta_max_chars:2000, ajustar_fuso:false, email_notificacao:''
    };
    const [form, setForm] = useState(emptyForm);
    const [headersText, setHeadersText] = useState('');

    useEffect(() => { load(); }, []);

    const load = async () => {
        try {
            const [i, f] = await Promise.all([api.get('/api/integracoes'), api.get('/api/funcionarios')]);
            setIntegracoes(i.data);
            setFuncs(f.data);
        } catch(e) { console.error(e); }
    };

    const openNew = () => {
        setEditing(null);
        setForm({...emptyForm, funcionario_id: funcs.length>0?funcs[0].id:''});
        setHeadersText('');
        setShowModal(true);
    };

    const openEdit = (integ) => {
        setEditing(integ);
        setForm({
            funcionario_id: integ.funcionario_id,
            nome: integ.nome,
            descricao: integ.descricao || '',
            tipo: integ.tipo,
            base_url: integ.base_url,
            auth_type: integ.auth_type,
            auth_config: integ.auth_config || {},
            headers_custom: integ.headers_custom || {},
            endpoints: integ.endpoints || [],
            descricao_ia: integ.descricao_ia,
            timeout_ms: integ.timeout_ms || 10000,
            retry_tentativas: integ.retry_tentativas || 1,
            resposta_max_chars: integ.resposta_max_chars || 2000,
            ajustar_fuso: integ.ajustar_fuso === true,
            email_notificacao: integ.email_notificacao || ''
        });
        setHeadersText(Object.keys(integ.headers_custom||{}).length > 0 ? JSON.stringify(integ.headers_custom, null, 2) : '');
        setShowModal(true);
    };

    const save = async () => {
        if (!form.funcionario_id || !form.nome || !form.base_url || !form.descricao_ia) {
            alert('Preencha: funcionario, nome, URL base e descricao para IA');
            return;
        }
        var payload = {...form};
        if (headersText.trim()) {
            try { payload.headers_custom = JSON.parse(headersText); } catch(e) { alert('Headers custom: JSON invalido'); return; }
        } else {
            payload.headers_custom = {};
        }
        try {
            if (editing) await api.put('/api/integracoes/' + editing.id, payload);
            else await api.post('/api/integracoes', payload);
            setShowModal(false);
            load();
        } catch(e) { alert('Erro: ' + (e.response?.data?.error || e.message)); }
    };

    const remove = async (id) => {
        if (!window.confirm('Remover esta integracao?')) return;
        try { await api.delete('/api/integracoes/' + id); load(); } catch(e) { alert('Erro'); }
    };

    const toggleAtivo = async (integ) => {
        try { await api.put('/api/integracoes/' + integ.id, { ativo: !integ.ativo }); load(); } catch(e) {}
    };

    // === Endpoints CRUD dentro do form ===
    const addEndpoint = () => {
        setForm({...form, endpoints: [...form.endpoints, {...emptyEndpoint}]});
    };

    const updateEndpoint = (idx, field, value) => {
        var eps = [...form.endpoints];
        eps[idx] = {...eps[idx], [field]: value};
        setForm({...form, endpoints: eps});
    };

    const removeEndpoint = (idx) => {
        var eps = [...form.endpoints];
        eps.splice(idx, 1);
        setForm({...form, endpoints: eps});
    };

    const addParam = (epIdx) => {
        var eps = [...form.endpoints];
        eps[epIdx].parametros = [...(eps[epIdx].parametros || []), {...emptyParam}];
        setForm({...form, endpoints: eps});
    };

    const updateParam = (epIdx, pIdx, field, value) => {
        var eps = [...form.endpoints];
        eps[epIdx].parametros[pIdx] = {...eps[epIdx].parametros[pIdx], [field]: value};
        setForm({...form, endpoints: eps});
    };

    const removeParam = (epIdx, pIdx) => {
        var eps = [...form.endpoints];
        eps[epIdx].parametros.splice(pIdx, 1);
        setForm({...form, endpoints: eps});
    };

    // === Testar endpoint ===
    const openTestar = (integ) => {
        setShowTestar(integ);
        setTestEndpoint(integ.endpoints?.length > 0 ? integ.endpoints[0].nome : '');
        setTestParams({});
        setTestResult(null);
    };

    const runTest = async () => {
        setTestLoading(true);
        setTestResult(null);
        try {
            const res = await api.post('/api/integracoes/' + showTestar.id + '/testar', {
                endpoint_nome: testEndpoint,
                parametros: testParams
            });
            setTestResult(res.data);
        } catch(e) {
            setTestResult({ sucesso: false, error: e.response?.data?.error || e.message });
        }
        setTestLoading(false);
    };

    const selectedTestEp = showTestar ? (showTestar.endpoints || []).find(e => e.nome === testEndpoint) : null;

    // === Auth config helpers ===
    const updateAuth = (field, value) => {
        setForm({...form, auth_config: {...form.auth_config, [field]: value}});
    };

    return (
        <div>
            <div className="page-header">
                <div>
                    <h1>Integracoes</h1>
                    <p style={{ color:'var(--text-secondary)', fontSize:14, marginTop:4 }}>APIs externas conectadas aos funcionarios IA</p>
                </div>
                <button className="btn-primary" onClick={openNew} style={{ display:'flex', alignItems:'center', gap:8 }}>
                    <Plus size={18}/> Nova integracao
                </button>
            </div>

            {integracoes.length === 0 ? (
                <div className="card" style={{ textAlign:'center', padding:60 }}>
                    <Plug size={40} color="var(--text-muted)" style={{ marginBottom:16 }}/>
                    <p style={{ color:'var(--text-muted)' }}>Nenhuma integracao configurada</p>
                    <p style={{ color:'var(--text-muted)', fontSize:13, marginTop:8 }}>Conecte APIs externas para seus funcionarios consultarem dados em tempo real</p>
                    <button className="btn-primary" onClick={openNew} style={{ marginTop:16 }}>Criar primeira integracao</button>
                </div>
            ) : (
                <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(360px, 1fr))', gap:16 }}>
                    {integracoes.map(integ => (
                        <div key={integ.id} className="card">
                            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:12 }}>
                                <div>
                                    <h3 style={{ fontSize:17, fontWeight:600 }}>{integ.nome}</h3>
                                    <p style={{ color:'var(--text-secondary)', fontSize:13, marginTop:2 }}>{integ.funcionario_nome} • {integ.cliente_nome}</p>
                                </div>
                                <span className={integ.ativo ? 'badge badge-online' : 'badge badge-offline'} style={{ cursor:'pointer' }} onClick={() => toggleAtivo(integ)}>
                                    {integ.ativo ? 'Ativo' : 'Inativo'}
                                </span>
                            </div>

                            <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginBottom:12 }}>
                                <span className="badge badge-purple">{integ.tipo}</span>
                                <span className="badge badge-amber">{integ.auth_type}</span>
                                <span className="badge" style={{ background:'rgba(127,119,221,0.1)', color:'var(--purple-400)' }}>
                                    {(integ.endpoints || []).length} endpoint{(integ.endpoints || []).length !== 1 ? 's' : ''}
                                </span>
                            </div>

                            <p style={{ color:'var(--text-muted)', fontSize:13, marginBottom:8 }}>{integ.base_url}</p>
                            <p style={{ color:'var(--text-muted)', fontSize:12, marginBottom:12, lineHeight:1.4, fontStyle:'italic' }}>{integ.descricao_ia?.substring(0, 150)}{integ.descricao_ia?.length > 150 ? '...' : ''}</p>

                            {/* Endpoints expandíveis */}
                            {(integ.endpoints || []).length > 0 && (
                                <div style={{ marginBottom:12 }}>
                                    <button className="btn-ghost" onClick={() => setExpandedCard(expandedCard === integ.id ? null : integ.id)} style={{ padding:'4px 8px', fontSize:12, display:'flex', alignItems:'center', gap:4 }}>
                                        {expandedCard === integ.id ? <ChevronUp size={14}/> : <ChevronDown size={14}/>}
                                        {expandedCard === integ.id ? 'Ocultar' : 'Ver'} endpoints
                                    </button>
                                    {expandedCard === integ.id && (
                                        <div style={{ marginTop:8, padding:12, background:'var(--bg-primary)', borderRadius:8, border:'1px solid var(--border)' }}>
                                            {integ.endpoints.map((ep, idx) => (
                                                <div key={idx} style={{ marginBottom: idx < integ.endpoints.length - 1 ? 10 : 0, paddingBottom: idx < integ.endpoints.length - 1 ? 10 : 0, borderBottom: idx < integ.endpoints.length - 1 ? '1px solid var(--border)' : 'none' }}>
                                                    <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                                                        <span style={{ fontSize:11, fontWeight:600, padding:'2px 6px', borderRadius:4, background: ep.metodo === 'GET' ? 'rgba(34,197,94,0.15)' : ep.metodo === 'POST' ? 'rgba(59,130,246,0.15)' : 'rgba(234,179,8,0.15)', color: ep.metodo === 'GET' ? 'var(--green-400)' : ep.metodo === 'POST' ? '#60a5fa' : '#facc15' }}>{ep.metodo}</span>
                                                        <span style={{ fontSize:13, fontWeight:500 }}>{ep.nome}</span>
                                                    </div>
                                                    <p style={{ fontSize:12, color:'var(--text-muted)', marginTop:4 }}>{ep.descricao}</p>
                                                    <p style={{ fontSize:11, color:'var(--text-muted)', marginTop:2, fontFamily:'monospace' }}>{ep.caminho}</p>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Stats */}
                            {(integ.total_chamadas > 0 || integ.total_erros > 0) && (
                                <div style={{ display:'flex', gap:16, fontSize:12, color:'var(--text-muted)', marginBottom:12 }}>
                                    <span>{integ.total_chamadas} chamadas</span>
                                    {integ.total_erros > 0 && <span style={{ color:'var(--red-500)' }}>{integ.total_erros} erros</span>}
                                    {integ.ultimo_uso && <span>Ultimo: {new Date(integ.ultimo_uso).toLocaleDateString('pt-BR')}</span>}
                                </div>
                            )}

                            <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
                                <button className="btn-ghost" onClick={() => openEdit(integ)} style={{ padding:'6px 12px', fontSize:13 }}><Edit2 size={14} style={{ marginRight:4 }}/> Editar</button>
                                <button className="btn-success" onClick={() => openTestar(integ)} style={{ padding:'6px 12px', fontSize:13 }}><Play size={14} style={{ marginRight:4 }}/> Testar</button>
                                <button className="btn-ghost" onClick={() => remove(integ.id)} style={{ padding:'6px 12px', fontSize:13, color:'var(--red-500)' }}><Trash2 size={14}/></button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* ====== MODAL CRIAR/EDITAR ====== */}
            {showModal && (
                <div className="modal-overlay" onClick={() => setShowModal(false)}>
                    <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth:800, maxHeight:'90vh', overflow:'auto' }}>
                        <h2>{editing ? 'Editar integracao' : 'Nova integracao'}</h2>

                        {/* Dados básicos */}
                        <div className="form-row">
                            <div className="form-group">
                                <label>Funcionario *</label>
                                <select value={form.funcionario_id} onChange={e => setForm({...form, funcionario_id: e.target.value})}>
                                    <option value="">Selecione...</option>
                                    {funcs.map(f => <option key={f.id} value={f.id}>{f.nome} ({f.cliente_nome})</option>)}
                                </select>
                            </div>
                            <div className="form-group">
                                <label>Nome da integracao *</label>
                                <input value={form.nome} onChange={e => setForm({...form, nome: e.target.value})} placeholder="Ex: Bling ERP, Agendor CRM"/>
                            </div>
                        </div>

                        <div className="form-group">
                            <label>Descricao</label>
                            <input value={form.descricao} onChange={e => setForm({...form, descricao: e.target.value})} placeholder="Descricao interna (opcional)"/>
                        </div>

                        <div className="form-row">
                            <div className="form-group">
                                <label>URL Base *</label>
                                <input value={form.base_url} onChange={e => setForm({...form, base_url: e.target.value})} placeholder="https://api.exemplo.com/v1"/>
                            </div>
                            <div className="form-group">
                                <label>Tipo de autenticacao</label>
                                <select value={form.auth_type} onChange={e => setForm({...form, auth_type: e.target.value, auth_config: {}})}>
                                    {AUTH_TYPES.map(a => <option key={a.value} value={a.value}>{a.label}</option>)}
                                </select>
                            </div>
                        </div>

                        {/* Auth config dinâmico */}
                        {form.auth_type === 'bearer' && (
                            <div className="form-group">
                                <label>Bearer Token</label>
                                <input type="password" value={form.auth_config.token || ''} onChange={e => updateAuth('token', e.target.value)} placeholder="Token de acesso"/>
                            </div>
                        )}
                        {form.auth_type === 'api_key' && (
                            <div className="form-row">
                                <div className="form-group">
                                    <label>Nome do header</label>
                                    <input value={form.auth_config.header || ''} onChange={e => updateAuth('header', e.target.value)} placeholder="X-API-Key"/>
                                </div>
                                <div className="form-group">
                                    <label>Valor</label>
                                    <input type="password" value={form.auth_config.value || ''} onChange={e => updateAuth('value', e.target.value)} placeholder="Sua API key"/>
                                </div>
                            </div>
                        )}
                        {form.auth_type === 'basic' && (
                            <div className="form-row">
                                <div className="form-group">
                                    <label>Usuario</label>
                                    <input value={form.auth_config.user || ''} onChange={e => updateAuth('user', e.target.value)}/>
                                </div>
                                <div className="form-group">
                                    <label>Senha</label>
                                    <input type="password" value={form.auth_config.pass || ''} onChange={e => updateAuth('pass', e.target.value)}/>
                                </div>
                            </div>
                        )}
                        {form.auth_type === 'custom_header' && (
                            <div className="form-group">
                                <label>Headers customizados (JSON)</label>
                                <textarea value={headersText} onChange={e => setHeadersText(e.target.value)} placeholder='{"X-Custom": "valor", "Another": "valor2"}' style={{ minHeight:80, fontFamily:'monospace', fontSize:13 }}/>
                            </div>
                        )}
                        {form.auth_type === 'oauth' && (
                            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                                <div className="form-group">
                                    <label>Token URL</label>
                                    <input value={form.auth_config.token_url || ''} onChange={e => setForm({...form, auth_config:{...form.auth_config, token_url: e.target.value}})} placeholder="https://api.exemplo.com/oauth/token"/>
                                </div>
                                <div className="form-group">
                                    <label>Grant Type</label>
                                    <input value={form.auth_config.grant_type || 'password'} onChange={e => setForm({...form, auth_config:{...form.auth_config, grant_type: e.target.value}})} placeholder="password"/>
                                </div>
                                <div className="form-group">
                                    <label>Client ID</label>
                                    <input value={form.auth_config.client_id || ''} onChange={e => setForm({...form, auth_config:{...form.auth_config, client_id: e.target.value}})} placeholder="89"/>
                                </div>
                                <div className="form-group">
                                    <label>Client Secret</label>
                                    <input value={form.auth_config.client_secret || ''} onChange={e => setForm({...form, auth_config:{...form.auth_config, client_secret: e.target.value}})} placeholder="ONe7Ns48Y30tB"/>
                                </div>
                                <div className="form-group">
                                    <label>Username</label>
                                    <input value={form.auth_config.username || ''} onChange={e => setForm({...form, auth_config:{...form.auth_config, username: e.target.value}})} placeholder="api@empresa.com"/>
                                </div>
                                <div className="form-group">
                                    <label>Password</label>
                                    <input type="password" value={form.auth_config.password || ''} onChange={e => setForm({...form, auth_config:{...form.auth_config, password: e.target.value}})} placeholder="senha"/>
                                </div>
                            </div>
                        )}

                        {/* Headers extras */}
                        {form.auth_type !== 'custom_header' && (
                            <div className="form-group">
                                <label>Headers extras (JSON, opcional)</label>
                                <textarea value={headersText} onChange={e => setHeadersText(e.target.value)} placeholder='{"Accept": "application/json"}' style={{ minHeight:60, fontFamily:'monospace', fontSize:13 }}/>
                            </div>
                        )}

                        {/* Descrição para IA */}
                        <div className="form-group">
                            <label>Descricao para a IA * <span style={{ fontWeight:400, color:'var(--text-muted)', fontSize:12 }}>(explique QUANDO a IA deve usar esta integracao)</span></label>
                            <textarea value={form.descricao_ia} onChange={e => setForm({...form, descricao_ia: e.target.value})} placeholder="Use esta integracao quando o cliente perguntar sobre produtos, estoque, precos ou disponibilidade. Consulte antes de responder sobre qualquer produto." style={{ minHeight:100 }}/>
                        </div>

                        {/* Configurações avançadas */}
                        <div className="form-row" style={{ gridTemplateColumns:'1fr 1fr 1fr 1fr' }}>
                            <div className="form-group">
                                <label>Timeout (ms)</label>
                                <input type="number" value={form.timeout_ms} onChange={e => setForm({...form, timeout_ms: parseInt(e.target.value) || 10000})}/>
                            </div>
                            <div className="form-group">
                                <label>Retentativas</label>
                                <input type="number" value={form.retry_tentativas} onChange={e => setForm({...form, retry_tentativas: parseInt(e.target.value) || 1})}/>
                            </div>
                            <div className="form-group">
                                <label>Max chars resposta</label>
                                <input type="number" value={form.resposta_max_chars} onChange={e => setForm({...form, resposta_max_chars: parseInt(e.target.value) || 2000})}/>
                            </div>
                            <div className="form-group">
                                <label>Fuso do agente</label>
                                <label style={{ display:'flex', alignItems:'center', gap:8, fontSize:13, cursor:'pointer', marginTop:8 }}>
                                    <input type="checkbox" checked={form.ajustar_fuso === true} onChange={e => setForm({...form, ajustar_fuso: e.target.checked})}/>
                                </label>
                            </div>
                            <div className="form-group">
                                <label>E-mail de notificação <span style={{ color:'var(--text-muted)', fontSize:11 }}>(recebe aviso dos endpoints marcados com Notificar)</span></label>
                                <input value={form.email_notificacao || ''} onChange={e => setForm({...form, email_notificacao: e.target.value})} placeholder="equipe@cliente.com"/>
                            </div>
                            <div style={{ display:'none' }}>
                                <label>
                                    <input type="checkbox" readOnly checked={false}/>
                                    Ajustar horarios ao fuso do agente
                                </label>
                            </div>
                        </div>

                        {/* ====== ENDPOINTS ====== */}
                        <div style={{ marginTop:20, paddingTop:20, borderTop:'1px solid var(--border)' }}>
                            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
                                <h3 style={{ fontSize:16, fontWeight:600 }}>Endpoints (ferramentas para a IA)</h3>
                                <button className="btn-ghost" onClick={addEndpoint} style={{ padding:'6px 12px', fontSize:13 }}><Plus size={14} style={{ marginRight:4 }}/> Adicionar endpoint</button>
                            </div>

                            {form.endpoints.length === 0 && (
                                <p style={{ color:'var(--text-muted)', fontSize:13, textAlign:'center', padding:20 }}>Nenhum endpoint configurado. Adicione pelo menos um para a IA poder usar esta integracao.</p>
                            )}

                            {form.endpoints.map((ep, epIdx) => (
                                <div key={epIdx} style={{ padding:16, marginBottom:12, background:'var(--bg-primary)', borderRadius:8, border:'1px solid var(--border)' }}>
                                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12 }}>
                                        <span style={{ fontSize:14, fontWeight:500 }}>Endpoint #{epIdx + 1}</span>
                                        <button className="btn-ghost" onClick={() => removeEndpoint(epIdx)} style={{ padding:'4px 8px', fontSize:12, color:'var(--red-500)' }}><Trash2 size={12}/></button>
                                    </div>

                                    <div className="form-row" style={{ gridTemplateColumns:'2fr 3fr 1fr' }}>
                                        <div className="form-group">
                                            <label style={{ fontSize:12 }}>Nome (ID)</label>
                                            <input value={ep.nome} onChange={e => updateEndpoint(epIdx, 'nome', e.target.value)} placeholder="buscar_produto" style={{ fontSize:13 }}/>
                                        </div>
                                        <div className="form-group">
                                            <label style={{ fontSize:12 }}>Descricao</label>
                                            <input value={ep.descricao} onChange={e => updateEndpoint(epIdx, 'descricao', e.target.value)} placeholder="Busca produto por nome ou codigo" style={{ fontSize:13 }}/>
                                        </div>
                                        <div className="form-group">
                                            <label style={{ fontSize:12 }}>Metodo</label>
                                            <select value={ep.metodo} onChange={e => updateEndpoint(epIdx, 'metodo', e.target.value)} style={{ fontSize:13 }}>
                                                {METODOS.map(m => <option key={m} value={m}>{m}</option>)}
                                            </select>
                                        </div>
                                    </div>

                                    <div className="form-row">
                                        <div className="form-group">
                                            <label style={{ fontSize:12 }}>Caminho</label>
                                            <input value={ep.caminho} onChange={e => updateEndpoint(epIdx, 'caminho', e.target.value)} placeholder="/produtos ou /contatos/{id}" style={{ fontSize:13, fontFamily:'monospace' }}/>
                                        </div>
                                        <div className="form-group">
                                            <label style={{ fontSize:12 }}>Mapeamento resposta</label>
                                            <input value={ep.mapeamento_resposta || ''} onChange={e => updateEndpoint(epIdx, 'mapeamento_resposta', e.target.value)} placeholder="data.items" style={{ fontSize:13, fontFamily:'monospace' }}/>
                                            <label style={{ fontSize:12, display:'block', marginTop:6 }}>
                                                <input type="checkbox" checked={ep.notificar === true} onChange={ev => updateEndpoint(epIdx, 'notificar', ev.target.checked)}/> Notificar por e-mail no sucesso
                                            </label>
                                            {ep.notificar === true && (
                                                <div style={{ marginTop:6 }}>
                                                    <label style={{ fontSize:12 }}>Assunto do e-mail (opcional)</label>
                                                    <input value={ep.email_assunto || ''} onChange={e => updateEndpoint(epIdx, 'email_assunto', e.target.value)} placeholder="Novo agendamento: {resposta.customer.first_name}" style={{ fontSize:13 }}/>
                                                    <label style={{ fontSize:12, display:'block', marginTop:6 }}>Modelo do e-mail (opcional — vazio = tabela dos parametros)</label>
                                                    <textarea value={ep.email_modelo || ''} onChange={e => updateEndpoint(epIdx, 'email_modelo', e.target.value)} rows={7} placeholder={'Cliente: {resposta.customer.first_name} {resposta.customer.last_name}\nHorario: {scheduled_start} a {scheduled_end|hora}'} style={{ fontSize:13, fontFamily:'monospace', width:'100%' }}/>
                                                    <div style={{ fontSize:11, color:'#888', marginTop:4 }}>Placeholders: {'{nome_do_parametro}'} · {'{resposta.campo.subcampo}'} (resposta da API) · sufixo |data ou |hora em datas · datas saem no fuso do agente</div>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Body template para POST/PUT/PATCH */}
                                    {['POST','PUT','PATCH'].includes(ep.metodo) && (
                                        <div className="form-group">
                                            <label style={{ fontSize:12 }}>Body template (JSON)</label>
                                            <textarea value={ep.body_template ? JSON.stringify(ep.body_template, null, 2) : ''} onChange={e => { try { updateEndpoint(epIdx, 'body_template', JSON.parse(e.target.value)); } catch(err) { updateEndpoint(epIdx, 'body_template', e.target.value); }}} placeholder='{"nome": "{nome}", "telefone": "{telefone}"}' style={{ minHeight:60, fontFamily:'monospace', fontSize:12 }}/>
                                        </div>
                                    )}

                                    {/* Parâmetros */}
                                    <div style={{ marginTop:8 }}>
                                        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8 }}>
                                            <label style={{ fontSize:12, fontWeight:500 }}>Parametros</label>
                                            <button className="btn-ghost" onClick={() => addParam(epIdx)} style={{ padding:'2px 8px', fontSize:11 }}><Plus size={12}/> Param</button>
                                        </div>
                                        {(ep.parametros || []).map((p, pIdx) => (
                                            <div key={pIdx} style={{ display:'grid', gridTemplateColumns:'2fr 1fr 3fr 1fr 1fr auto', gap:6, marginBottom:6, alignItems:'end' }}>
                                                <input value={p.nome} onChange={e => updateParam(epIdx, pIdx, 'nome', e.target.value)} placeholder="nome" style={{ fontSize:12, padding:'6px 8px' }}/>
                                                <select value={p.in || 'query'} onChange={e => updateParam(epIdx, pIdx, 'in', e.target.value)} style={{ fontSize:12, padding:'6px 4px' }}>
                                                    <option value="query">query</option>
                                                    <option value="path">path</option>
                                                    <option value="body">body</option>
                                                </select>
                                                <input value={p.descricao} onChange={e => updateParam(epIdx, pIdx, 'descricao', e.target.value)} placeholder="Descricao do parametro" style={{ fontSize:12, padding:'6px 8px' }}/>
                                                <select value={p.tipo || 'string'} onChange={e => updateParam(epIdx, pIdx, 'tipo', e.target.value)} style={{ fontSize:12, padding:'6px 4px' }}>
                                                    <option value="string">string</option>
                                                    <option value="number">number</option>
                                                    <option value="boolean">boolean</option>
                                                </select>
                                                <label style={{ display:'flex', alignItems:'center', gap:4, fontSize:11, cursor:'pointer' }}>
                                                    <input type="checkbox" checked={p.obrigatorio || false} onChange={e => updateParam(epIdx, pIdx, 'obrigatorio', e.target.checked)}/> Req
                                                </label>
                                                <button onClick={() => removeParam(epIdx, pIdx)} style={{ background:'none', border:'none', color:'var(--red-500)', cursor:'pointer', padding:4 }}><Trash2 size={12}/></button>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div className="modal-actions">
                            <button className="btn-ghost" onClick={() => setShowModal(false)}>Cancelar</button>
                            <button className="btn-primary" onClick={save}>{editing ? 'Salvar' : 'Criar integracao'}</button>
                        </div>
                    </div>
                </div>
            )}

            {/* ====== MODAL TESTAR ====== */}
            {showTestar && (
                <div className="modal-overlay" onClick={() => setShowTestar(false)}>
                    <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth:650, maxHeight:'80vh', overflow:'auto' }}>
                        <h2>Testar: {showTestar.nome}</h2>
                        <p style={{ color:'var(--text-secondary)', fontSize:13, marginBottom:16 }}>{showTestar.base_url}</p>

                        <div className="form-group">
                            <label>Endpoint</label>
                            <select value={testEndpoint} onChange={e => { setTestEndpoint(e.target.value); setTestParams({}); setTestResult(null); }}>
                                {(showTestar.endpoints || []).map(ep => (
                                    <option key={ep.nome} value={ep.nome}>{ep.metodo} {ep.nome} - {ep.descricao}</option>
                                ))}
                            </select>
                        </div>

                        {selectedTestEp && (selectedTestEp.parametros || []).length > 0 && (
                            <div style={{ marginBottom:16 }}>
                                <label style={{ fontSize:13, fontWeight:500, marginBottom:8, display:'block' }}>Parametros</label>
                                {selectedTestEp.parametros.map((p, idx) => (
                                    <div key={idx} className="form-group" style={{ marginBottom:8 }}>
                                        <label style={{ fontSize:12 }}>{p.nome} {p.obrigatorio ? '*' : ''} <span style={{ color:'var(--text-muted)' }}>({p.tipo}, {p.in || 'query'})</span></label>
                                        <input value={testParams[p.nome] || ''} onChange={e => setTestParams({...testParams, [p.nome]: e.target.value})} placeholder={p.descricao} style={{ fontSize:13 }}/>
                                    </div>
                                ))}
                            </div>
                        )}

                        <button className="btn-primary" onClick={runTest} disabled={testLoading} style={{ marginBottom:16 }}>
                            {testLoading ? 'Testando...' : 'Executar teste'}
                        </button>

                        {testResult && (
                            <div style={{ padding:16, borderRadius:8, border:'1px solid var(--border)', background:'var(--bg-primary)' }}>
                                <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:12 }}>
                                    {testResult.sucesso ? <CheckCircle size={18} color="var(--green-400)"/> : <XCircle size={18} color="var(--red-500)"/>}
                                    <span style={{ fontWeight:600, color: testResult.sucesso ? 'var(--green-400)' : 'var(--red-500)' }}>
                                        {testResult.sucesso ? 'Sucesso' : 'Erro'}
                                    </span>
                                    {testResult.status && <span className="badge badge-purple">HTTP {testResult.status}</span>}
                                    {testResult.tempo_ms && <span style={{ fontSize:12, color:'var(--text-muted)' }}>{testResult.tempo_ms}ms</span>}
                                </div>
                                <pre style={{ fontSize:12, color:'var(--text-secondary)', overflow:'auto', maxHeight:300, whiteSpace:'pre-wrap', wordBreak:'break-all', margin:0, padding:12, background:'var(--bg-secondary)', borderRadius:6 }}>
                                    {testResult.sucesso ? JSON.stringify(testResult.dados, null, 2) : testResult.error}
                                </pre>
                            </div>
                        )}

                        <div className="modal-actions">
                            <button className="btn-ghost" onClick={() => setShowTestar(false)}>Fechar</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
